import { takeUntil, filter } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { createSmileyDefaults } from './smiley-defaults.factory.js';
import { DEFAULT_SMILEY_ICON, type SmileyIcon } from './smiley-icons.config.js';

/**
 * Subscribes to `place/smiley` UiActions and materialises a new smiley:
 * builds the SmileyObject via `createSmileyDefaults`, pushes an
 * `AddObjectCommand` onto history, then re-emits `selection/set` for the
 * new id (PRD-F3 §TR1, §TR4).
 *
 * Lifecycle: subscription is bound to `bus.destroy$` via `takeUntil`. The
 * controller is NOT a Lit ReactiveController — its lifetime equals the bus.
 *
 * FC: never publishes onto `selection$`/`tool$`/`viewport$` directly; the
 * downstream selection adapter translates `selection/set` into a
 * `selection$` write.
 *
 * Smileys have NO inline editor — the controller MUST NOT dispatch any
 * `edit/enter-*` action (Acceptance §AC4 / FC analog Person but inverted).
 *
 * Supports both bare-click placement (no `size`, smiley centred on pointer)
 * and drag-to-create (with `size`, pointer = top-left of dragged box).
 */
export class SmileyPlacementController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;

    constructor(bus: WhiteboardBus, state: BoardState, history: History<BoardState>) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/smiley' }> =>
                        a.type === 'place/smiley',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[SmileyPlacementController] bus error swallowed');
                    }
                },
            });
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/smiley' }>): void {
        const scale = this.#bus.viewport$.value.scale;
        const sizeOpt = action.size ?? null;
        // Stefan, 2026-09-03 — "choose properties before drawing", see the
        // identical comment in rectangle-placement.controller.ts. Unlike the
        // other factories, `createSmileyDefaults` already HAD this override
        // parameter (its 4th, bare `icon: SmileyIcon`) since before this
        // feature — nothing upstream ever populated it. This is the fix.
        // Bugreport (Anke, 2026-09-03): "beim icon ist fast nichts korrekt
        // angeschlossen" — `fill` (the motif recolour) was never read here.
        const staged = this.#bus.stagedToolProperties$.value.smiley;
        const icon = (staged?.['icon'] as SmileyIcon | undefined) ?? DEFAULT_SMILEY_ICON;
        const fill = staged?.['fill'] as string | undefined;
        const smiley = createSmileyDefaults(
            scale,
            { x: action.worldX, y: action.worldY },
            sizeOpt,
            icon,
            fill,
        );

        const cmd = new AddObjectCommand(smiley);
        this.#history.push(cmd, this.#state);

        const newId = objectId(smiley.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
    }
}
